/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useRef, useState} from 'react';
import {ActivateHandle, PasswordErrors} from "../../../util/interfaces";
import {AppContext} from "../../misc/appContext";
import User from "../../../models/userModel";
import {getJwt, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import PasswordSettings from "./passwordSettings";
import {onSetCrumbs} from "../../../util/events";
import ValidateTwoFactorController from "../../twoFactor/validate/validateTwoFactorController";
import {disable2Fa} from "../../../util/requestSender";
import {createErrorMessage} from "../../../util/utils";
import EnableTwoFactorController from "../../twoFactor/enable/enableTwoFactorController";
import PageChangePermission from "../../misc/pageChangePermission";
import {PermissionCallback} from "../../../util/pageChanger";
import {JWTCookieName} from "../../../util/jwtUtil";

const EMPTY_ERRORS: PasswordErrors = {confirmPassword: null, newPassword: null, password: null};

export default function PasswordSettingsController() {
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<PasswordErrors>(EMPTY_ERRORS);
    const [mfaEnabled, setMfaEnabled] = useState(user?.twoFactorEnabled ?? false);
    const mfaRef = useRef<ActivateHandle>(null);
    const enableMfaRef = useRef<ActivateHandle>(null);
    const hasChanged = useRef(false);

    function onChange(key: 'new' | 'confirm' | 'pass') {
        return function(value: string) {
            hasChanged.current = true;
            if (key === 'new') setNewPassword(value);
            else if (key === 'confirm') setConfirmPassword(value);
            else setPassword(value);
        }
    }

    function onResetError(key: keyof PasswordErrors) {
        return function() {
            setErrors(Object.assign({}, errors, {[key]: null}));
        }
    }

    function onDenyPageChange(id: number | null, callback: PermissionCallback) {
        if (hasChanged.current)
            onSubmit(() => {
                callback(id, true);
            })();
        else callback(id, true);
    }

    function onSubmit(callback?: VoidFunction) {
        return function() {
            if (!validateValues()) return;
            const {user} = context.userContext;
            if (!user) return;
            setLoading(true);
            user.changePassword(password, newPassword, (user: User, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    hasChanged.current = false;
                    showSnackbar('Je wachtwoord is gewijzigd');
                    context.onChangeContext({user});
                    setPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    callback?.();
                } else showDialog(createErrorMessage(data.message ?? 'Het wijzigen van je wachtwoord is niet gelukt.'),
                    'Wijzigen mislukt');
            });
        }
    }

    function onToggleTwoFactor(enabled: boolean) {
        if (enabled) enableMfaRef.current?.activate();
        else {
            const jwtToken = getJwt(JWTCookieName) ?? '';
            mfaRef.current?.activate(jwtToken);
        }
    }

    function onTwoFactorSuccess(enabled: boolean) {
        return function(user: User) {
            if (enabled) return setMfaEnabled(user.twoFactorEnabled);
            disable2Fa({
                callback: (data: ResponseData) => {
                    if (data.success) {
                        showSnackbar('Tweestapsverificatie is uitgeschakeld',
                            {backgroundColor: 'color-green-whatsapp'});
                        setMfaEnabled(user.twoFactorEnabled);
                    } else
                        showDialog(createErrorMessage(data.message ??
                            'Het uitschakelen van de tweestapsverificatie is niet gelukt.'), 'Uitschakelen mislukt');
                }
            })
        }
    }

    function validateValues() {
        let valid: boolean = true;
        const _errors = Object.assign({}, errors);
        if (!password) {
            valid = false;
            _errors.password = 'Vul je huidige wachtwoord in';
        }
        if (!newPassword) {
            valid = false;
            _errors.newPassword = 'Vul een nieuwe wachtwoord in';
        }
        if (!confirmPassword) {
            valid = false;
            _errors.confirmPassword = 'Bevestig het nieuwe wachtwoord';
        } else if (confirmPassword !== newPassword) {
            valid = false;
            _errors.confirmPassword = 'De wachtwoorden komen niet overeen';
        }
        if (!valid) setErrors(_errors);
        return valid;
    }

    useEffect(() => {
        onSetCrumbs([{label: 'Instellingen'}, {label: 'Beveiliging'}]);
    }, []);

    return (
        <>
            <PasswordSettings onChange={onChange} onSubmit={onSubmit()} onResetError={onResetError}
                              newPassword={newPassword} confirmPassword={confirmPassword} password={password}
                              loading={loading} errors={errors} twoFactorEnabled={mfaEnabled}
                              onToggleTwoFactor={onToggleTwoFactor}/>
            <ValidateTwoFactorController onSuccess={onTwoFactorSuccess(false)}
                                         explanation='Voer de tweestapsverificatie code in om deze uit te schakelen.'
                                         ref={mfaRef}/>
            <EnableTwoFactorController onSuccess={onTwoFactorSuccess(true)} ref={enableMfaRef}/>
            <PageChangePermission changed={hasChanged.current} onDeny={onDenyPageChange}
                                  denyText='Ja, wijzigingen opslaan' allowText='Nee, pagina verlaten'
                                  allowSecondary={true}>
                De wijzigingen gaan verloren als je de pagina verlaat. Wil je de wijzigingen eerst opslaan?
            </PageChangePermission>
        </>
    );
}